import { Card, Icon, Image, NoFound, Spinner } from '@/components/generic';
import { temporaryTiers } from '@/constants';
import { useGetReferralRulesForUser } from '@package/smart-wallet/hooks';

const ReferralRulesPanel = () => {
  const { isLoading, referralType, rules } = useGetReferralRulesForUser();

  if (isLoading) {
    return (
      <Card title='Your Referral Benefits' className='mb-3'>
        <div className='flex items-center justify-center py-20'>
          <Spinner />
        </div>
      </Card>
    );
  }

  if (!referralType) {
    return null;
  }

  return (
    <Card title='Your Referral Benefits' className='mb-3'>
      {/* Referral Type Header */}
      <div className='px-5 py-4 border-b border-s-stroke2'>
        <div className='flex items-center gap-3'>
          <div className='flex items-center justify-center w-10 h-10 rounded-lg bg-primary-02/10'>
            <Icon className='w-5 h-5 fill-primary-02' name='star' />
          </div>
          <div>
            <div className='text-h6 text-t-primary'>{referralType.name}</div>
            {referralType.description && (
              <div className='text-caption text-t-secondary'>
                {referralType.description}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className='p-5'>
          <NoFound
            title='No rules available'
            sub='There are no referral rules configured for your account type.'
          />
        </div>
      ) : (
        <div className='p-5'>
          <div className='mb-3 text-sub-title-2 text-t-secondary'>
            Earn commissions based on the tier of cards your referrals activate:
          </div>
          <div className='flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-s-stroke2 scrollbar-track-transparent'>
            {rules.map((rule: any) => (
              <div
                key={rule.id}
                className='card m-0 p-5 min-w-[280px] max-w-[400px] hover:shadow-lg transition-shadow duration-200'
              >
                {/* Card Image and Tier Name */}
                <div className='mb-5'>
                  <div className='flex justify-center mb-3'>
                    <Image
                      src={
                        temporaryTiers[
                          (rule.tier?.name as keyof typeof temporaryTiers) ||
                            'Tier 0'
                        ]?.img
                      }
                      alt={rule.tier?.name || `Tier ${rule.tierId}`}
                      className='w-1/2 h-auto rounded-xl'
                    />
                  </div>
                  <div className='text-center'>
                    <div className='text-h6 font-semibold text-t-primary'>
                      {rule.tier?.name || `Tier ${rule.tierId}`}
                    </div>
                    <div className='text-caption text-t-tertiary'>
                      Card Tier
                    </div>
                  </div>
                </div>

                {/* Card Creation Benefits */}
                {(rule.level1CreationBonus > 0 ||
                  rule.level2CreationBonus > 0) && (
                  <div className='mb-4'>
                    <div className='flex items-center gap-2 mb-3'>
                      <Icon className='w-4 h-4 fill-t-secondary' name='card' />
                      <div className='text-sub-title-2 font-semibold text-t-secondary'>
                        Card Creation
                      </div>
                    </div>
                    <div className='space-y-2'>
                      {rule.level1CreationBonus > 0 && (
                        <div className='flex items-start gap-2 pl-6'>
                          <div className='flex items-center justify-center w-5 h-5 rounded-full bg-green-500/10 shrink-0 mt-0.5'>
                            <Icon
                              className='w-2.5 h-2.5 fill-green-500'
                              name='check'
                            />
                          </div>
                          <div className='flex-1'>
                            <div className='text-body-2 text-t-primary'>
                              <span className='font-semibold text-green-600'>
                                ${rule.level1CreationBonus}
                              </span>{' '}
                              Level 1 bonus
                            </div>
                            <div className='text-caption text-t-tertiary'>
                              When direct referral creates a card
                            </div>
                          </div>
                        </div>
                      )}

                      {rule.level2CreationBonus > 0 && (
                        <div className='flex items-start gap-2 pl-6'>
                          <div className='flex items-center justify-center w-5 h-5 rounded-full bg-green-500/10 shrink-0 mt-0.5'>
                            <Icon
                              className='w-2.5 h-2.5 fill-green-500'
                              name='check'
                            />
                          </div>
                          <div className='flex-1'>
                            <div className='text-body-2 text-t-primary'>
                              <span className='font-semibold text-green-600'>
                                ${rule.level2CreationBonus}
                              </span>{' '}
                              Level 2 bonus
                            </div>
                            <div className='text-caption text-t-tertiary'>
                              When indirect referral creates a card
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Card Deposit Benefits */}
                <div className='mb-4'>
                  <div className='flex items-center gap-2 mb-3'>
                    <Icon className='w-4 h-4 fill-t-secondary' name='wallet' />
                    <div className='text-sub-title-2 font-semibold text-t-secondary'>
                      Card Deposits
                    </div>
                  </div>
                  <div className='space-y-2'>
                    <div className='flex items-start gap-2 pl-6'>
                      <div className='flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/10 shrink-0 mt-0.5'>
                        <Icon
                          className='w-2.5 h-2.5 fill-blue-500'
                          name='check'
                        />
                      </div>
                      <div className='flex-1'>
                        <div className='text-body-2 text-t-primary'>
                          <span className='font-semibold text-primary-02'>
                            {rule.level1Percentage}%
                          </span>{' '}
                          Level 1 commission
                        </div>
                        <div className='text-caption text-t-tertiary'>
                          On direct referral top-ups
                        </div>
                      </div>
                    </div>

                    <div className='flex items-start gap-2 pl-6'>
                      <div className='flex items-center justify-center w-5 h-5 rounded-full bg-purple-500/10 shrink-0 mt-0.5'>
                        <Icon
                          className='w-2.5 h-2.5 fill-purple-500'
                          name='check'
                        />
                      </div>
                      <div className='flex-1'>
                        <div className='text-body-2 text-t-primary'>
                          <span className='font-semibold text-primary-02'>
                            {rule.level2Percentage}%
                          </span>{' '}
                          Level 2 commission
                        </div>
                        <div className='text-caption text-t-tertiary'>
                          On indirect referral top-ups
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description if available */}
                {rule.description && (
                  <div className='pt-3 border-t border-s-stroke2'>
                    <div className='text-caption text-t-secondary'>
                      {rule.description}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ReferralRulesPanel;
